import { useState, useEffect, useCallback } from 'react'
import { profileService } from '../../services/profileService'
import { getDisplayDomain } from '../../utils/url'
import EditableField from './EditableField'
import ProfileImageUpload from './ProfileImageUpload'
import type { Profile, ProfileInput } from '../../types'

interface ProfileBasicInfoProps {
  profile: Profile
  onUpdate: (profile: Profile) => void
}

const USERNAME_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/

function validateUsernameFormat(username: string): string | null {
  if (username.length < 2) return 'Must be at least 2 characters'
  if (username.length > 30) return 'Must be at most 30 characters'
  if (!USERNAME_REGEX.test(username)) {
    return 'Only lowercase letters, numbers, and single hyphens (not at start/end)'
  }
  return null
}

export default function ProfileBasicInfo({ profile, onUpdate }: ProfileBasicInfoProps) {
  const siteName = getDisplayDomain()
  const [usernameInput, setUsernameInput] = useState(profile.username)
  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  >('idle')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [isSavingUsername, setIsSavingUsername] = useState(false)

  const checkUsername = useCallback(
    async (username: string) => {
      const normalized = username.toLowerCase().trim()

      // Check format first
      const formatError = validateUsernameFormat(normalized)
      if (formatError) {
        setUsernameStatus('invalid')
        setUsernameError(formatError)
        return
      }

      // If same as current, it's available
      if (normalized === profile.username) {
        setUsernameStatus('available')
        setUsernameError(null)
        return
      }

      setUsernameStatus('checking')
      setUsernameError(null)

      try {
        const { available } = await profileService.checkUsernameAvailability(normalized)
        setUsernameStatus(available ? 'available' : 'taken')
        setUsernameError(available ? null : 'This username is taken')
      } catch {
        setUsernameStatus('idle')
        setUsernameError('Failed to check availability')
      }
    },
    [profile.username]
  )

  useEffect(() => {
    if (!isEditingUsername) return

    const timer = setTimeout(() => {
      if (usernameInput.trim()) {
        checkUsername(usernameInput)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [usernameInput, isEditingUsername, checkUsername])

  const handleSaveUsername = async () => {
    const normalized = usernameInput.toLowerCase().trim()
    if (usernameStatus !== 'available' || normalized === profile.username) {
      setIsEditingUsername(false)
      setUsernameInput(profile.username)
      return
    }

    setIsSavingUsername(true)
    try {
      const updated = await profileService.updateProfile({
        username: normalized,
      })
      onUpdate(updated)
      setIsEditingUsername(false)
    } catch {
      setUsernameError('Failed to save username')
    } finally {
      setIsSavingUsername(false)
    }
  }

  const handleCancelUsername = () => {
    setUsernameInput(profile.username)
    setIsEditingUsername(false)
    setUsernameStatus('idle')
    setUsernameError(null)
  }

  const handleSave = async (field: keyof ProfileInput, value: string) => {
    const updated = await profileService.updateProfile({
      [field]: value || null,
    })
    onUpdate(updated)
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-indigo-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        Basic Information
      </h2>

      {/* Profile Image */}
      <ProfileImageUpload
        imageUrl={profile.profile_image_url}
        name={profile.full_name}
        onUpdate={onUpdate}
      />

      {/* Username Field */}
      <div className="mb-6 pb-6 border-b border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
        {isEditingUsername ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value.toLowerCase())}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  usernameStatus === 'available'
                    ? 'border-green-300 focus:ring-green-500'
                    : usernameStatus === 'taken' || usernameStatus === 'invalid'
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-200 focus:ring-indigo-500'
                }`}
                placeholder="your-username"
                maxLength={30}
              />
              <span className="text-gray-500">.{siteName}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm">
                {usernameStatus === 'checking' && (
                  <span className="text-gray-500">Checking availability...</span>
                )}
                {usernameStatus === 'available' && usernameInput !== profile.username && (
                  <span className="text-green-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Available
                  </span>
                )}
                {(usernameStatus === 'taken' || usernameStatus === 'invalid') && usernameError && (
                  <span className="text-red-600">{usernameError}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelUsername}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  disabled={isSavingUsername}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUsername}
                  disabled={
                    usernameStatus !== 'available' ||
                    isSavingUsername ||
                    usernameInput === profile.username
                  }
                  className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingUsername ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-gray-900">{profile.username}</span>
              <span className="text-gray-500">.{siteName}</span>
            </div>
            <button
              onClick={() => setIsEditingUsername(true)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <EditableField
          label="First Name"
          value={profile.first_name}
          onSave={(value) => handleSave('first_name', value)}
          required
        />
        <EditableField
          label="Last Name"
          value={profile.last_name}
          onSave={(value) => handleSave('last_name', value)}
          required
        />
        <EditableField
          label="Headline"
          value={profile.headline}
          onSave={(value) => handleSave('headline', value)}
          placeholder="e.g. Senior Software Engineer"
        />
        <EditableField
          label="Email"
          value={profile.email}
          type="email"
          onSave={(value) => handleSave('email', value)}
        />
        <EditableField
          label="Phone"
          value={profile.phone}
          type="tel"
          onSave={(value) => handleSave('phone', value)}
        />
        <EditableField
          label="City"
          value={profile.city}
          onSave={(value) => handleSave('city', value)}
        />
        <EditableField
          label="State"
          value={profile.state}
          onSave={(value) => handleSave('state', value)}
        />
        <EditableField
          label="Country"
          value={profile.country}
          onSave={(value) => handleSave('country', value)}
        />
      </div>

      <div className="mt-6 space-y-6">
        <EditableField
          label="Summary"
          value={profile.summary}
          type="textarea"
          onSave={(value) => handleSave('summary', value)}
          placeholder="Write a brief summary about yourself..."
        />
      </div>

      <div className="mt-6 pt-6 border-t border-gray-100">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Social Links</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <EditableField
            label="LinkedIn"
            value={profile.linkedin_url}
            type="url"
            onSave={(value) => handleSave('linkedin_url', value)}
            placeholder="https://linkedin.com/in/username"
          />
          <EditableField
            label="GitHub"
            value={profile.github_url}
            type="url"
            onSave={(value) => handleSave('github_url', value)}
            placeholder="https://github.com/username"
          />
          <EditableField
            label="Twitter"
            value={profile.twitter_url}
            type="url"
            onSave={(value) => handleSave('twitter_url', value)}
            placeholder="https://twitter.com/username"
          />
          <EditableField
            label="Instagram"
            value={profile.instagram_url}
            type="url"
            onSave={(value) => handleSave('instagram_url', value)}
            placeholder="https://instagram.com/username"
          />
          <EditableField
            label="YouTube"
            value={profile.youtube_url}
            type="url"
            onSave={(value) => handleSave('youtube_url', value)}
            placeholder="https://youtube.com/@channel"
          />
          <EditableField
            label="Dribbble"
            value={profile.dribbble_url}
            type="url"
            onSave={(value) => handleSave('dribbble_url', value)}
            placeholder="https://dribbble.com/username"
          />
          <EditableField
            label="Behance"
            value={profile.behance_url}
            type="url"
            onSave={(value) => handleSave('behance_url', value)}
            placeholder="https://behance.net/username"
          />
          <EditableField
            label="Medium"
            value={profile.medium_url}
            type="url"
            onSave={(value) => handleSave('medium_url', value)}
            placeholder="https://medium.com/@username"
          />
          <EditableField
            label="Stack Overflow"
            value={profile.stackoverflow_url}
            type="url"
            onSave={(value) => handleSave('stackoverflow_url', value)}
            placeholder="https://stackoverflow.com/users/id"
          />
          <EditableField
            label="Website"
            value={profile.website_url}
            type="url"
            onSave={(value) => handleSave('website_url', value)}
            placeholder="https://yourwebsite.com"
          />
        </div>
      </div>
    </div>
  )
}
